import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  CheckCircle,
  RadioButtonUnchecked,
  Assignment,
  Security,
  People,
  Description,
  Warning,
  TrendingUp,
  Code,
  CloudDone,
  Link as LinkIcon
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { odosModuleService, ODOSModule } from '../../services/odosService';
import TeamEditDialog from '../../components/odos/TeamEditDialog';
import ModuleEditDialog from '../../components/odos/ModuleEditDialog';
import EditableFeatureCard from '../../components/odos/EditableFeatureCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ODOSModuleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [module, setModule] = useState<ODOSModule | null>(null);
  const [editTeamOpen, setEditTeamOpen] = useState(false);
  const [editModuleOpen, setEditModuleOpen] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [editingFeatureIndex, setEditingFeatureIndex] = useState<number | null>(null);

  useEffect(() => {
    if (id) {
      loadModule();
      loadUsers();
    }
  }, [id]);

  const loadModule = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await odosModuleService.getModule(id!);
      setModule(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load module');
      console.error('Error loading module:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await odosModuleService.getUsers();
      setUsers(response.data || []);
    } catch (err: any) {
      console.error('Error loading users:', err);
    }
  };

  const handleTeamSave = async (teamData: any) => {
    try {
      await odosModuleService.updateModule(id!, { team: teamData });
      await loadModule(); // Refresh module data
      setEditTeamOpen(false);
    } catch (err: any) {
      throw err; // Let dialog handle the error
    }
  };

  const handleModuleSave = async (moduleData: any) => {
    try {
      await odosModuleService.updateModule(id!, moduleData);
      await loadModule(); // Refresh module data
      setEditModuleOpen(false);
    } catch (err: any) {
      throw err; // Let dialog handle the error
    }
  };

  const handleFeatureSave = async (featureIndex: number, updatedFeature: any) => {
    if (!module) return;

    try {
      console.log('=== Feature Save Debug ===');
      console.log('Feature Index:', featureIndex);
      console.log('Updated Feature:', updatedFeature);
      console.log('Status value:', updatedFeature.status);
      console.log('Priority value:', updatedFeature.priority);

      // Clean the feature object - ensure all fields are properly set
      const cleanFeature = {
        name: updatedFeature.name || '',
        title: updatedFeature.title || updatedFeature.name || '',
        description: updatedFeature.description || '',
        userStory: updatedFeature.userStory || '',
        userStoryDetails: {
          role: updatedFeature.userStoryDetails?.role || '',
          need: updatedFeature.userStoryDetails?.need || '',
          benefit: updatedFeature.userStoryDetails?.benefit || ''
        },
        originalDescription: updatedFeature.originalDescription || updatedFeature.description || '',
        status: updatedFeature.status, // Ensure status is explicitly set
        priority: updatedFeature.priority // Ensure priority is explicitly set
      };

      console.log('Clean Feature:', cleanFeature);
      console.log('Sending to API using updateFeatureByIndex...');

      // Use the new endpoint that updates by index - this uses MongoDB's positional update
      const result = await odosModuleService.updateFeatureByIndex(id!, featureIndex, cleanFeature);
      
      console.log('API Response:', result);
      console.log('=== Save Complete ===');
      
      // Refresh module data to get the latest from database
      await loadModule();
      setEditingFeatureIndex(null);
    } catch (err: any) {
      console.error('=== Error Saving Feature ===');
      console.error('Error:', err);
      console.error('Error Response:', err.response?.data);
      console.error('Error Message:', err.message);
      throw err;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'in-development': return 'info';
      case 'testing': return 'warning';
      case 'planning': return 'default';
      case 'deployed': return 'success';
      case 'maintenance': return 'warning';
      case 'deprecated': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading module details...</Typography>
      </Container>
    );
  }

  if (error || !module) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Module not found'}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/odos')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/odos" underline="hover" color="inherit">
          ODOS Dashboard
        </MuiLink>
        <Typography color="text.primary">{module.name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <IconButton onClick={() => navigate('/odos')} aria-label="Back to dashboard">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
              {module.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 7, flexWrap: 'wrap' }}>
            <Chip label={module.moduleId} size="small" />
            <Chip label={module.category} size="small" color="primary" />
            <Chip label={module.status} size="small" color={getStatusColor(module.status)} />
            {module.versionString && <Chip label={`v${module.versionString}`} size="small" variant="outlined" />}
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => setEditModuleOpen(true)}
          sx={{ bgcolor: '#003366', '&:hover': { bgcolor: '#002244' } }}
        >
          Edit Module
        </Button>
      </Box>

      {/* Description */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1" paragraph>
            {module.description}
          </Typography>
          {module.pwsReference && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    PWS Reference: Section {module.pwsReference?.section}, Page {module.pwsReference?.page}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {module.pwsReference?.requirement}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/pws/${module.pwsReference?.section}/${module.pwsReference?.page}`)}
                  sx={{ borderColor: '#003366', color: '#003366' }}
                >
                  View PWS Content
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Progress Card */}
      {module.features && module.features.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Overall Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={module.completionPercentage || 0}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Typography variant="h6" sx={{ minWidth: 60, textAlign: 'right' }}>
                {module.completionPercentage || 0}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {module.features.filter(f => f.status === 'completed').length} of {module.features.length} features completed
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="Module details tabs">
          <Tab label="Features" icon={<Assignment />} iconPosition="start" />
          <Tab label="Technical Stack" icon={<Code />} iconPosition="start" />
          <Tab label="Compliance" icon={<Security />} iconPosition="start" />
          <Tab label="Team" icon={<People />} iconPosition="start" />
          <Tab label="Deliverables" icon={<Description />} iconPosition="start" />
          <Tab label="Risks & Issues" icon={<Warning />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Features Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {module.features && module.features.length > 0 ? (
            module.features.map((feature, index) => (
              <Grid item xs={12} md={6} key={index}>
                <EditableFeatureCard
                  feature={feature}
                  index={index}
                  onSave={handleFeatureSave}
                  isEditing={editingFeatureIndex === index}
                  onEditStart={(idx) => setEditingFeatureIndex(idx)}
                  onEditCancel={() => setEditingFeatureIndex(null)}
                  getPriorityColor={getPriorityColor}
                />
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  No features defined yet
                </Typography>
                <Typography variant="body2">
                  This module was auto-generated from the PWS. Features were extracted from requirements, but you can add more by clicking "Edit Module".
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Technical Stack Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {module.technicalStack ? (
            <>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Frontend
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {module.technicalStack.frontend?.map((tech, i) => (
                        <Chip key={i} label={tech} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Backend
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {module.technicalStack.backend?.map((tech, i) => (
                        <Chip key={i} label={tech} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Database
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {module.technicalStack.database?.map((tech, i) => (
                        <Chip key={i} label={tech} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Infrastructure
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {module.technicalStack.infrastructure?.map((tech, i) => (
                        <Chip key={i} label={tech} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Integrations
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {module.technicalStack.integrations?.map((tech, i) => (
                        <Chip key={i} label={tech} />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  No technical stack defined yet
                </Typography>
                <Typography variant="body2">
                  This module was auto-generated from the PWS document. Click "Edit Module" to add:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                  <li>Frontend technologies (React, Angular, etc.)</li>
                  <li>Backend technologies (Node.js, Java, etc.)</li>
                  <li>Database systems (MongoDB, PostgreSQL, etc.)</li>
                  <li>Infrastructure tools (Docker, Kubernetes, etc.)</li>
                  <li>Integration points</li>
                </Box>
              </Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Compliance Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {!module.compliance && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Compliance tracking not yet configured
                </Typography>
                <Typography variant="body2">
                  As this module progresses, compliance requirements (FISMA, FedRAMP, Section 508, SOC 2, NIST) will be tracked here. These checkboxes show the current compliance status.
                </Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Compliance Status
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      {module.compliance?.fisma ? <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                    </ListItemIcon>
                    <ListItemText primary="FISMA" secondary="Federal Information Security Management Act" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {module.compliance?.fedramp ? <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                    </ListItemIcon>
                    <ListItemText primary="FedRAMP" secondary="Federal Risk and Authorization Management Program" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {module.compliance?.section508 ? <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                    </ListItemIcon>
                    <ListItemText primary="Section 508" secondary="Accessibility Requirements" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {module.compliance?.soc2 ? <CheckCircle color="success" /> : <RadioButtonUnchecked />}
                    </ListItemIcon>
                    <ListItemText primary="SOC 2" secondary="Service Organization Control" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          {module.compliance?.nist && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    NIST Controls
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Framework: {module.compliance.nist.framework}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {module.compliance.nist.controls?.map((control, i) => (
                      <Chip key={i} label={control} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Team Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Team Members
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditTeamOpen(true)}
                    sx={{
                      borderColor: '#003366',
                      color: '#003366',
                      '&:hover': {
                        borderColor: '#003366',
                        bgcolor: 'rgba(0, 51, 102, 0.05)'
                      }
                    }}
                  >
                    Edit Team
                  </Button>
                </Box>
                {module.team?.lead && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#003366', mb: 1 }}>
                      Team Lead
                    </Typography>
                    <Typography variant="body2">
                      {typeof module.team.lead === 'object' && module.team.lead !== null && 'name' in module.team.lead
                        ? (module.team.lead as any).name
                        : module.team.lead}
                    </Typography>
                  </Box>
                )}
                {module.team?.members && module.team.members.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Responsibilities</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {module.team.members.map((member, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {typeof member.user === 'object' && member.user !== null && 'name' in member.user
                                ? (member.user as any).name
                                : member.user}
                            </TableCell>
                            <TableCell>{member.role}</TableCell>
                            <TableCell>
                              {member.responsibilities?.join(', ') || 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    No team members assigned yet. Click "Edit Team" to add team members.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
          {module.team?.stakeholders && module.team.stakeholders.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Stakeholders
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Organization</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {module.team.stakeholders.map((stakeholder, index) => (
                          <TableRow key={index}>
                            <TableCell>{stakeholder.name}</TableCell>
                            <TableCell>{stakeholder.organization}</TableCell>
                            <TableCell>{stakeholder.role}</TableCell>
                            <TableCell>{stakeholder.email}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Deliverables Tab */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          {module.deliverables && module.deliverables.length > 0 ? (
            module.deliverables.map((deliverable, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {deliverable.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {deliverable.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Chip label={deliverable.type} size="small" />
                      <Chip label={deliverable.status} size="small" color="primary" />
                      {deliverable.clin && <Chip label={deliverable.clin} size="small" variant="outlined" />}
                    </Box>
                    {deliverable.dueDate && (
                      <Typography variant="caption" color="text.secondary">
                        Due: {new Date(deliverable.dueDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  No deliverables defined yet
                </Typography>
                <Typography variant="body2">
                  Deliverables will be added as the module progresses through implementation phases. Check the PWS document for expected deliverables.
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Risks & Issues Tab */}
      <TabPanel value={tabValue} index={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="error">
                  Risks
                </Typography>
                {module.risks && module.risks.length > 0 ? (
                  <List>
                    {module.risks.map((risk, index) => (
                      <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2">{risk.description}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip label={`Impact: ${risk.impact}`} size="small" color="error" />
                          <Chip label={`Probability: ${risk.probability}`} size="small" />
                          <Chip label={risk.status} size="small" variant="outlined" />
                        </Box>
                        {risk.mitigation && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Mitigation: {risk.mitigation}
                          </Typography>
                        )}
                        {index < module.risks!.length - 1 && <Divider sx={{ width: '100%', mt: 2 }} />}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="success">No risks identified</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="warning.main">
                  Issues
                </Typography>
                {module.issues && module.issues.length > 0 ? (
                  <List>
                    {module.issues.map((issue, index) => (
                      <ListItem key={index} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2">{issue.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {issue.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Chip label={issue.severity} size="small" color="warning" />
                          <Chip label={issue.status} size="small" />
                        </Box>
                        {index < module.issues!.length - 1 && <Divider sx={{ width: '100%', mt: 2 }} />}
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Alert severity="success">No open issues</Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Team Edit Dialog */}
      <TeamEditDialog
        open={editTeamOpen}
        onClose={() => setEditTeamOpen(false)}
        module={module}
        onSave={handleTeamSave}
        users={users}
      />

      {/* Module Edit Dialog */}
      <ModuleEditDialog
        open={editModuleOpen}
        onClose={() => setEditModuleOpen(false)}
        module={module}
        onSave={handleModuleSave}
      />
    </Container>
  );
};

export default ODOSModuleDetail;
